import {
  Button,
  ButtonGroup,
  DataStatus,
  DialogFooter,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  showNotification,
} from 'platform/components';
import {Grid, Text, VStack} from 'platform/foundation';

import {isNil} from 'ramda';

import {useGetTenantQuery} from '@dms/api/core';
import {
  CustomerServiceContractRequestBody,
  PatchCustomerServiceContractArg,
  PostCustomerServiceContractArg,
  useGetCustomerServiceContractQuery,
  usePatchCustomerServiceContractMutation,
  usePostCustomerServiceContractMutation,
} from '@dms/api/customer';
import {useGetAuthorizationProfilesQuery} from '@dms/api/security';
import i18n from '@dms/i18n';
import {getOptionsFromAuthorizationProfiles, handleApiError, useTenant} from '@dms/shared';

import {getApiDateString, parseDate, suffixTestId, TestIdProps} from 'shared/web';

import {CustomerOrderFormSchema, CustomerOrderForm} from '../types/CustomerOrderForm';

interface CustomerOrderProps extends TestIdProps {
  customerId: string;
  serviceContractId?: string;
  submitButtonTitle?: string;
  onClose: () => void;
  onSubmitSuccess: () => void;
}

export function CustomerOrder(props: CustomerOrderProps) {
  const {tenantCurrency} = useTenant();
  const {data: tenant} = useGetTenantQuery();

  const {
    data: serviceContractData,
    isLoading: isServiceContractLoading,
    isError: isServiceContractError,
  } = useGetCustomerServiceContractQuery(
    {
      customerId: props.customerId,
      serviceContractId: props.serviceContractId ?? '',
    },
    {skip: isNil(props.serviceContractId)}
  );

  const {
    data: authorizationProfiles,
    isLoading: isAuthorizationProfilesLoading,
    isError: isAuthorizationProfilesError,
  } = useGetAuthorizationProfilesQuery({'x-tenant': tenant?.id ?? ''}, {skip: isNil(tenant)});

  const [postCustomerServiceContract, {isLoading: isCreateCustomerServiceContractLoading}] =
    usePostCustomerServiceContractMutation();
  const [patchCustomerServiceContract, {isLoading: isPatchCustomerServiceContractLoading}] =
    usePatchCustomerServiceContractMutation();

  const handleCreateCustomerServiceContract = async (body: CustomerServiceContractRequestBody) => {
    const request: PostCustomerServiceContractArg = {
      customerId: props.customerId,
      body,
    };

    await postCustomerServiceContract(request)
      .unwrap()
      .then(() => {
        showNotification.info(i18n.t('entity.customer.customerOrder.saveSuccess'));
        props.onSubmitSuccess();
        props.onClose();
      })
      .catch(handleApiError);
  };

  const handlePatchCustomerServiceContract = async (body: CustomerServiceContractRequestBody) => {
    const request: PatchCustomerServiceContractArg = {
      customerId: props.customerId,
      serviceContractId: props.serviceContractId ?? '',
      body,
    };

    await patchCustomerServiceContract(request)
      .unwrap()
      .then(() => {
        showNotification.info(i18n.t('entity.customer.customerOrder.saveSuccess'));
        props.onSubmitSuccess();
        props.onClose();
      })
      .catch(handleApiError);
  };

  const handleSubmit: FormSubmitHandler<CustomerOrderForm> = async (values, _setErrors) => {
    const body: CustomerServiceContractRequestBody = {
      number: values.number,
      authorizationProfileIds: values.authorizationProfileIds,
      validFrom: values.validFrom ? getApiDateString(values.validFrom) : undefined,
      validTo: values.validTo ? getApiDateString(values.validTo) : undefined,
      value: values.value ?? undefined,
      note: values.note ?? undefined,
    };

    if (props.serviceContractId) {
      await handlePatchCustomerServiceContract(body);
    } else {
      await handleCreateCustomerServiceContract(body);
    }
  };

  const getDefaultValues = (): CustomerOrderForm | undefined => {
    if (isNil(props.serviceContractId) || !serviceContractData) {
      return undefined;
    }
    return {
      number: serviceContractData.number,
      authorizationProfileIds: serviceContractData.authorizationProfileIds ?? [],
      validFrom: parseDate(serviceContractData.validFrom),
      validTo: parseDate(serviceContractData.validTo),
      value: serviceContractData.value,
      remainingValue: serviceContractData.remainingValue,
      note: serviceContractData.note,
    };
  };

  const isError = isServiceContractError || isAuthorizationProfilesError;
  const isLoading = isServiceContractLoading || isAuthorizationProfilesLoading;
  const isSubmitting =
    isCreateCustomerServiceContractLoading || isPatchCustomerServiceContractLoading;
  const submitButtonTitle = props.submitButtonTitle ?? i18n.t('general.actions.create');

  return (
    <DataStatus isLoading={isLoading} isError={isError}>
      <Form<CustomerOrderForm>
        defaultValues={getDefaultValues()}
        experimentalZodSchema={CustomerOrderFormSchema}
        onSubmit={handleSubmit}
      >
        {(control, formApi) => (
          <VStack spacing={4}>
            <Text size="small" color="secondary">
              {i18n.t('entity.customer.customerOrder.description')}
            </Text>
            <Grid columns={[1, 2]}>
              <FormField
                isRequired
                name="number"
                type="text"
                control={control}
                label={i18n.t('entity.customer.customerOrder.number')}
                data-testid={suffixTestId('number', props)}
              />
              <FormField
                isRequired
                name="authorizationProfileIds"
                type="multiChoice"
                control={control}
                options={getOptionsFromAuthorizationProfiles(
                  authorizationProfiles,
                  formApi.watch('authorizationProfileIds')
                )}
                label={i18n.t('entity.customer.customerOrder.authorizationProfileIds')}
                data-testid={suffixTestId('authorizationProfileIds', props)}
              />
            </Grid>
            <Grid columns={[1, 2]}>
              <FormField
                name="validFrom"
                type="date"
                control={control}
                label={i18n.t('entity.customer.customerOrder.validFrom')}
                data-testid={suffixTestId('validFrom', props)}
              />
              <FormField
                name="validTo"
                type="date"
                control={control}
                label={i18n.t('entity.customer.customerOrder.validTo')}
                data-testid={suffixTestId('validTo', props)}
              />
            </Grid>
            <Grid columns={[1, 2]}>
              <FormField
                name="value"
                type="currency"
                currency={tenantCurrency}
                control={control}
                decimalPlaces={2}
                label={i18n.t('entity.customer.customerOrder.value')}
                data-testid={suffixTestId('value', props)}
              />
              <FormField
                name="remainingValue"
                type="currency"
                isDisabled
                control={control}
                decimalPlaces={2}
                currency={tenantCurrency}
                label={i18n.t('entity.customer.customerOrder.remainingValue')}
                data-testid={suffixTestId('remainingValue', props)}
              />
            </Grid>
            <Grid columns={1}>
              <FormField
                name="note"
                type="textarea"
                control={control}
                label={i18n.t('entity.customer.customerOrder.note')}
                data-testid={suffixTestId('note', props)}
              />
            </Grid>
            <DialogFooter>
              <ButtonGroup align="right">
                <Button
                  title={i18n.t('general.actions.discard')}
                  variant="secondary"
                  onClick={props.onClose}
                  isLoading={isSubmitting}
                  isDisabled={isSubmitting}
                  data-testid={suffixTestId('discard', props)}
                />
                <FormButton
                  control={control}
                  type="submit"
                  title={submitButtonTitle}
                  isLoading={isSubmitting}
                  isDisabled={isSubmitting}
                  data-testid={suffixTestId('create', props)}
                />
              </ButtonGroup>
            </DialogFooter>
          </VStack>
        )}
      </Form>
    </DataStatus>
  );
}
